import { useEffect, useState } from "react";

/** 모바일 판정 기준 폭(px) — CRM 레이아웃/페이지 공용. topNav 모바일 브레이크(900px)보다 좁은
 * "폰 화면" 전용 기준으로, 사이드바 오버레이·컬럼 축소 등 레이아웃 재배치에 사용한다. */
export const MOBILE_MAX_WIDTH = 768;

/** "세로 공간이 모자란" 기준 높이(px).
 *
 * ★ 폭만 보면 안 되는 이유: 폰을 눕히면 **844×390** 이라 `max-width: 768px` 판정이
 *   통째로 빠진다. 그래서 가로모드에서 "고정 높이 + overflow:hidden" 3열 콘솔이
 *   되살아나 본문이 32px 로 찌그러지고 아래 내용에 닿을 수 없었다(실측).
 *   세로 공간이 문제인 화면은 `useIsCompactViewport()` 를 써야 한다.
 *   `src/styles/mobile.css` 의 `(max-width: 768px), (max-height: 560px)` 와 짝이다. */
export const COMPACT_MAX_HEIGHT = 560;

const COMPACT_QUERY = `(max-width: ${MOBILE_MAX_WIDTH}px), (max-height: ${COMPACT_MAX_HEIGHT}px)`;

/** SSR 안전 1회성 판정 — useState 초기값 등 훅을 쓸 수 없는 곳에서 사용 */
export function isMobileViewport(maxWidth: number = MOBILE_MAX_WIDTH): boolean {
  return typeof window !== "undefined" && window.matchMedia(`(max-width: ${maxWidth}px)`).matches;
}

/** SSR 안전 1회성 판정 — 좁거나(폰 세로) **낮은**(폰 가로) 화면 */
export function isCompactViewport(): boolean {
  return typeof window !== "undefined" && window.matchMedia(COMPACT_QUERY).matches;
}

/** 공통 구독 — 미디어 쿼리 하나를 반응형으로 추적 */
function useMediaQuery(query: string, initial: () => boolean): boolean {
  const [matched, setMatched] = useState(initial);

  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setMatched(e.matches);
    setMatched(mq.matches);   // 쿼리가 바뀌었을 수 있으니 구독 시점에 한 번 맞춘다
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);

  return matched;
}

/** 뷰포트가 maxWidth 이하인지 반응형으로 추적 — 회전/리사이즈 시 자동 갱신 */
export default function useIsMobile(maxWidth: number = MOBILE_MAX_WIDTH): boolean {
  return useMediaQuery(`(max-width: ${maxWidth}px)`, () => isMobileViewport(maxWidth));
}

/** 좁거나 **낮은** 화면인지 — "고정 높이 셸 해제 / 세로 스택" 판정에 쓴다.
 *  폰 세로(390×844)와 폰 가로(844×390)를 **둘 다** 잡는다. */
export function useIsCompactViewport(): boolean {
  return useMediaQuery(COMPACT_QUERY, isCompactViewport);
}
